// ─────────────────────────────────────────────────────────────
// API cliente del módulo DEPORTES
// baseURL de axiosInstance ya incluye `/api`, por lo que aquí las
// rutas comienzan en `/deportes/...`.
// ─────────────────────────────────────────────────────────────
import axiosInstance from './AxiosInstance';

// ============ Tipos ============
export interface Disciplina {
  id: number;
  codigo: string;
  nombre: string;
  descripcion?: string | null;
  activo?: boolean;
}

export interface Categoria {
  id: number;
  codigo: string;
  nombre: string;
  descripcion?: string | null;
  edadMinima?: number | null;
  edadMaxima?: number | null;
  activo?: boolean;
}

export interface Rama {
  id: number;
  codigo: string;
  nombre: string;
  activo?: boolean;
}

export interface CategoriaInventario {
  id: number;
  codigo: string;
  nombre: string;
  descripcion?: string | null;
  activo?: boolean;
}

export interface Torneo {
  id: number;
  nombre: string;
  disciplinaId: number;
  categoriaId?: number | null;
  ramaId?: number | null;
  fechaInicio?: string | null;
  fechaConclusion?: string | null;
  responsable?: string | null;
  descripcion?: string | null;
  puntosVictoria?: number;
  puntosEmpate?: number;
  puntosDerrota?: number;
  calculaPosiciones?: boolean;
  sede?: string | null;
  estado?: string | null;
  municipio?: string | null;
  localidadId?: number | null;
  localidad?: string | null;
  colonia?: string | null;
  codigoPostal?: string | null;
  tipoVialidad?: string | null;
  vialidad?: string | null;
  numeroExterior?: string | null;
  numeroInterior?: string | null;
  referencias?: string | null;
  latitud?: number | null;
  longitud?: number | null;
  urlMaps?: string | null;
  estatus?: string;
  disciplina?: Disciplina;
  categoria?: Categoria;
  rama?: Rama;
  equipos?: Equipo[];
  archivos?: ArchivoGenerico[];
}

export interface Equipo {
  id: number;
  torneoId: number;
  nombre: string;
  entrenadorRepresentante?: string | null;
  contacto?: string | null;
  observaciones?: string | null;
  estatus?: string;
}

export interface Encuentro {
  id: number;
  torneoId: number;
  equipoLocalId?: number | null;
  equipoVisitanteId?: number | null;
  jornada?: number | null;
  fecha?: string | null;
  hora?: string | null;
  sede?: string | null;
  golesLocal?: number | null;
  golesVisitante?: number | null;
  resultado?: string | null;
  observaciones?: string | null;
  estatus?: string;
  equipoLocal?: Equipo;
  equipoVisitante?: Equipo;
}

export interface FilaTabla {
  equipoId: number;
  equipo: string;
  pj: number;
  pg: number;
  pe: number;
  pp: number;
  gf: number;
  gc: number;
  dif: number;
  pts: number;
}

export interface TablaPosiciones {
  origen: 'CALCULADA' | 'MANUAL';
  torneoId: number;
  puntos?: { victoria: number; empate: number; derrota: number };
  tabla: FilaTabla[];
}

export interface ArchivoGenerico {
  id: number;
  nombreArchivo: string;
  rutaArchivo: string;
  tipoArchivo?: string | null;
  mimeType?: string | null;
  descripcion?: string | null;
  createdAt?: string;
}

export interface Deportista {
  id: number;
  nombre: string;
  apPaterno?: string | null;
  apMaterno?: string | null;
  fechaNacimiento?: string | null;
  edad?: number | null;
  sexo?: string | null;
  curp?: string | null;
  telefono?: string | null;
  celular?: string | null;
  email?: string | null;
  fotografia?: string | null;
  estado?: string | null;
  municipio?: string | null;
  localidadId?: number | null;
  localidad?: string | null;
  colonia?: string | null;
  codigoPostal?: string | null;
  tipoVialidad?: string | null;
  vialidad?: string | null;
  numeroExterior?: string | null;
  numeroInterior?: string | null;
  referencias?: string | null;
  disciplinaId?: number | null;
  categoriaId?: number | null;
  equipoId?: number | null;
  entrenador?: string | null;
  nivel?: string | null;
  institucionEducativa?: string | null;
  observaciones?: string | null;
  estatus?: string;
  disciplina?: Disciplina;
  categoria?: Categoria;
  equipo?: Equipo;
}

export interface Participacion {
  id: number;
  deportistaId: number;
  torneoId: number;
  equipoId?: number | null;
  fecha?: string | null;
  logros?: string | null;
  observaciones?: string | null;
  torneo?: Torneo;
  equipo?: Equipo;
  deportista?: Deportista;
}

export interface Convocatoria {
  id: number;
  folio?: string | null;
  nombre: string;
  descripcion?: string | null;
  disciplinaId?: number | null;
  fechaPublicacion?: string | null;
  fechaCierre?: string | null;
  responsable?: string | null;
  requisitos?: string | null;
  publicadaPortal?: boolean;
  permiteRegistro?: boolean;
  estatus?: string;
  disciplina?: Disciplina;
  categorias?: any[];
  archivos?: ArchivoGenerico[];
}

export interface Inscripcion {
  id: number;
  convocatoriaId: number;
  deportistaId?: number | null;
  folio?: string | null;
  nombre: string;
  apPaterno?: string | null;
  apMaterno?: string | null;
  fechaNacimiento?: string | null;
  sexo?: string | null;
  curp?: string | null;
  telefono?: string | null;
  email?: string | null;
  localidad?: string | null;
  localidadId?: number | null;
  observaciones?: string | null;
  estatus?: string;
  origenPortal?: boolean;
  fecha?: string | null;
  convocatoria?: Convocatoria;
  deportista?: Deportista;
}

export interface Articulo {
  id: number;
  numeroInventario: string;
  nombre: string;
  categoriaId: number;
  descripcion?: string | null;
  marca?: string | null;
  modelo?: string | null;
  serie?: string | null;
  cantidad?: number;
  cantidadDisponible?: number;
  unidad?: string | null;
  estadoFisico?: string;
  ubicacion?: string | null;
  responsableResguardo?: string | null;
  fechaAdquisicion?: string | null;
  proveedor?: string | null;
  costo?: number | null;
  observaciones?: string | null;
  estatus?: string;
  categoria?: CategoriaInventario;
  archivos?: ArchivoGenerico[];
  movimientos?: Movimiento[];
}

export interface Movimiento {
  id: number;
  articuloId: number;
  tipo: string;
  cantidad: number;
  existenciaPrevia?: number | null;
  existenciaNueva?: number | null;
  fecha?: string;
  responsable?: string | null;
  usuario?: string | null;
  observaciones?: string | null;
}

const cfgMultipart = { headers: { 'Content-Type': 'multipart/form-data' } };

// limpia parámetros vacíos
const params = (o: Record<string, any> = {}) => {
  const p: Record<string, any> = {};
  Object.entries(o).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p[k] = v;
  });
  return p;
};

// ============ TORNEOS ============
export const deportesApi = {
  // -- Torneos --
  listarTorneos: (filtros?: Record<string, any>) =>
    axiosInstance.get<Torneo[]>('/deportes/torneos', { params: params(filtros) }).then((r) => r.data),
  obtenerTorneo: (id: number) =>
    axiosInstance.get<Torneo>(`/deportes/torneos/${id}`).then((r) => r.data),
  crearTorneo: (data: Partial<Torneo>) =>
    axiosInstance.post<Torneo>('/deportes/torneos', data).then((r) => r.data),
  actualizarTorneo: (id: number, data: Partial<Torneo>) =>
    axiosInstance.put<Torneo>(`/deportes/torneos/${id}`, data).then((r) => r.data),
  eliminarTorneo: (id: number) =>
    axiosInstance.delete(`/deportes/torneos/${id}`).then((r) => r.data),
  obtenerTablaPosiciones: (id: number) =>
    axiosInstance.get<TablaPosiciones>(`/deportes/torneos/${id}/tabla`).then((r) => r.data),
  reporteTorneosPdf: (filtros?: Record<string, any>) =>
    axiosInstance.get('/deportes/torneos/reporte/pdf', { params: params(filtros), responseType: 'blob' }).then((r) => r.data),

  // -- Equipos --
  listarEquipos: (torneoId: number) =>
    axiosInstance.get<Equipo[]>(`/deportes/torneos/${torneoId}/equipos`).then((r) => r.data),
  crearEquipo: (torneoId: number, data: Partial<Equipo>) =>
    axiosInstance.post<Equipo>(`/deportes/torneos/${torneoId}/equipos`, data).then((r) => r.data),
  actualizarEquipo: (id: number, data: Partial<Equipo>) =>
    axiosInstance.put<Equipo>(`/deportes/torneos/equipos/${id}`, data).then((r) => r.data),
  eliminarEquipo: (id: number) =>
    axiosInstance.delete(`/deportes/torneos/equipos/${id}`).then((r) => r.data),

  // -- Encuentros --
  listarEncuentros: (torneoId: number) =>
    axiosInstance.get<Encuentro[]>(`/deportes/torneos/${torneoId}/encuentros`).then((r) => r.data),
  crearEncuentro: (torneoId: number, data: Partial<Encuentro>) =>
    axiosInstance.post<Encuentro>(`/deportes/torneos/${torneoId}/encuentros`, data).then((r) => r.data),
  actualizarEncuentro: (id: number, data: Partial<Encuentro>) =>
    axiosInstance.put<Encuentro>(`/deportes/torneos/encuentros/${id}`, data).then((r) => r.data),
  registrarResultado: (id: number, data: Partial<Encuentro>) =>
    axiosInstance.patch<Encuentro>(`/deportes/torneos/encuentros/${id}/resultado`, data).then((r) => r.data),
  eliminarEncuentro: (id: number) =>
    axiosInstance.delete(`/deportes/torneos/encuentros/${id}`).then((r) => r.data),

  // -- Posiciones (manual) --
  listarPosiciones: (torneoId: number) =>
    axiosInstance.get(`/deportes/torneos/${torneoId}/posiciones`).then((r) => r.data),
  guardarPosicion: (torneoId: number, data: any) =>
    axiosInstance.post(`/deportes/torneos/${torneoId}/posiciones`, data).then((r) => r.data),
  eliminarPosicion: (id: number) =>
    axiosInstance.delete(`/deportes/torneos/posiciones/${id}`).then((r) => r.data),

  // -- Archivos de torneo (evidencias) --
  listarTorneoArchivos: (torneoId: number) =>
    axiosInstance.get<ArchivoGenerico[]>(`/deportes/torneos/${torneoId}/archivos`).then((r) => r.data),
  subirTorneoArchivo: (torneoId: number, formData: FormData) =>
    axiosInstance.post(`/deportes/torneos/${torneoId}/archivos`, formData, cfgMultipart).then((r) => r.data),
  eliminarTorneoArchivo: (id: number) =>
    axiosInstance.delete(`/deportes/torneos/archivos/${id}`).then((r) => r.data),

  // ============ DEPORTISTAS ============
  listarDeportistas: (filtros?: Record<string, any>) =>
    axiosInstance.get<Deportista[]>('/deportes/deportistas', { params: params(filtros) }).then((r) => r.data),
  obtenerDeportista: (id: number) =>
    axiosInstance.get<Deportista>(`/deportes/deportistas/${id}`).then((r) => r.data),
  crearDeportista: (formData: FormData) =>
    axiosInstance.post<Deportista>('/deportes/deportistas', formData, cfgMultipart).then((r) => r.data),
  actualizarDeportista: (id: number, formData: FormData) =>
    axiosInstance.put<Deportista>(`/deportes/deportistas/${id}`, formData, cfgMultipart).then((r) => r.data),
  eliminarDeportista: (id: number) =>
    axiosInstance.delete(`/deportes/deportistas/${id}`).then((r) => r.data),
  historialDeportista: (id: number) =>
    axiosInstance.get<Participacion[]>(`/deportes/deportistas/${id}/participaciones`).then((r) => r.data),
  reporteDeportistasPdf: (filtros?: Record<string, any>) =>
    axiosInstance.get('/deportes/deportistas/reporte/pdf', { params: params(filtros), responseType: 'blob' }).then((r) => r.data),

  // ============ PARTICIPACIONES ============
  listarParticipaciones: (filtros?: Record<string, any>) =>
    axiosInstance.get<Participacion[]>('/deportes/participaciones', { params: params(filtros) }).then((r) => r.data),
  crearParticipacion: (data: Partial<Participacion>) =>
    axiosInstance.post<Participacion>('/deportes/participaciones', data).then((r) => r.data),
  actualizarParticipacion: (id: number, data: Partial<Participacion>) =>
    axiosInstance.put<Participacion>(`/deportes/participaciones/${id}`, data).then((r) => r.data),
  eliminarParticipacion: (id: number) =>
    axiosInstance.delete(`/deportes/participaciones/${id}`).then((r) => r.data),

  // ============ CONVOCATORIAS ============
  listarConvocatorias: (filtros?: Record<string, any>) =>
    axiosInstance.get<Convocatoria[]>('/deportes/convocatorias', { params: params(filtros) }).then((r) => r.data),
  obtenerConvocatoria: (id: number) =>
    axiosInstance.get<Convocatoria>(`/deportes/convocatorias/${id}`).then((r) => r.data),
  crearConvocatoria: (data: Partial<Convocatoria>) =>
    axiosInstance.post<Convocatoria>('/deportes/convocatorias', data).then((r) => r.data),
  actualizarConvocatoria: (id: number, data: Partial<Convocatoria>) =>
    axiosInstance.put<Convocatoria>(`/deportes/convocatorias/${id}`, data).then((r) => r.data),
  eliminarConvocatoria: (id: number) =>
    axiosInstance.delete(`/deportes/convocatorias/${id}`).then((r) => r.data),
  publicarConvocatoria: (id: number) =>
    axiosInstance.patch(`/deportes/convocatorias/${id}/publicar`).then((r) => r.data),
  despublicarConvocatoria: (id: number) =>
    axiosInstance.patch(`/deportes/convocatorias/${id}/despublicar`).then((r) => r.data),
  listarConvocatoriaCategorias: (id: number) =>
    axiosInstance.get(`/deportes/convocatorias/${id}/categorias`).then((r) => r.data),
  agregarConvocatoriaCategoria: (id: number, categoriaId: number) =>
    axiosInstance.post(`/deportes/convocatorias/${id}/categorias`, { categoriaId }).then((r) => r.data),
  eliminarConvocatoriaCategoria: (id: number) =>
    axiosInstance.delete(`/deportes/convocatorias/categorias/${id}`).then((r) => r.data),
  listarConvocatoriaArchivos: (id: number) =>
    axiosInstance.get<ArchivoGenerico[]>(`/deportes/convocatorias/${id}/archivos`).then((r) => r.data),
  subirConvocatoriaArchivo: (id: number, formData: FormData) =>
    axiosInstance.post(`/deportes/convocatorias/${id}/archivos`, formData, cfgMultipart).then((r) => r.data),
  eliminarConvocatoriaArchivo: (id: number) =>
    axiosInstance.delete(`/deportes/convocatorias/archivos/${id}`).then((r) => r.data),
  inscripcionesDeConvocatoria: (id: number, filtros?: Record<string, any>) =>
    axiosInstance.get<Inscripcion[]>(`/deportes/convocatorias/${id}/inscripciones`, { params: params(filtros) }).then((r) => r.data),

  // ============ INSCRIPCIONES ============
  listarInscripciones: (filtros?: Record<string, any>) =>
    axiosInstance.get<Inscripcion[]>('/deportes/inscripciones', { params: params(filtros) }).then((r) => r.data),
  obtenerInscripcion: (id: number) =>
    axiosInstance.get<Inscripcion>(`/deportes/inscripciones/${id}`).then((r) => r.data),
  crearInscripcion: (data: Partial<Inscripcion>) =>
    axiosInstance.post<Inscripcion>('/deportes/inscripciones', data).then((r) => r.data),
  aceptarInscripcion: (id: number) =>
    axiosInstance.patch(`/deportes/inscripciones/${id}/aceptar`).then((r) => r.data),
  rechazarInscripcion: (id: number) =>
    axiosInstance.patch(`/deportes/inscripciones/${id}/rechazar`).then((r) => r.data),
  eliminarInscripcion: (id: number) =>
    axiosInstance.delete(`/deportes/inscripciones/${id}`).then((r) => r.data),

  // ============ INVENTARIO ============
  listarArticulos: (filtros?: Record<string, any>) =>
    axiosInstance.get<Articulo[]>('/deportes/inventario', { params: params(filtros) }).then((r) => r.data),
  obtenerArticulo: (id: number) =>
    axiosInstance.get<Articulo>(`/deportes/inventario/${id}`).then((r) => r.data),
  reporteInventarioPdf: (filtros?: Record<string, any>) =>
    axiosInstance.get('/deportes/inventario/reporte/pdf', { params: params(filtros), responseType: 'blob' }).then((r) => r.data),
  crearArticulo: (data: Partial<Articulo>) =>
    axiosInstance.post<Articulo>('/deportes/inventario', data).then((r) => r.data),
  actualizarArticulo: (id: number, data: Partial<Articulo>) =>
    axiosInstance.put<Articulo>(`/deportes/inventario/${id}`, data).then((r) => r.data),
  eliminarArticulo: (id: number) =>
    axiosInstance.delete(`/deportes/inventario/${id}`).then((r) => r.data),
  listarArticuloFotos: (id: number) =>
    axiosInstance.get<ArchivoGenerico[]>(`/deportes/inventario/${id}/fotos`).then((r) => r.data),
  subirArticuloFoto: (id: number, formData: FormData) =>
    axiosInstance.post(`/deportes/inventario/${id}/fotos`, formData, cfgMultipart).then((r) => r.data),
  eliminarArticuloFoto: (id: number) =>
    axiosInstance.delete(`/deportes/inventario/fotos/${id}`).then((r) => r.data),
  listarMovimientos: (id: number) =>
    axiosInstance.get<Movimiento[]>(`/deportes/inventario/${id}/movimientos`).then((r) => r.data),
  crearMovimiento: (id: number, data: Partial<Movimiento>) =>
    axiosInstance.post<Movimiento>(`/deportes/inventario/${id}/movimientos`, data).then((r) => r.data),

  // ============ CATÁLOGOS ============
  // recurso: 'disciplinas' | 'categorias' | 'ramas' | 'categoria-inventario'
  listarCatalogo: (recurso: string, filtros?: Record<string, any>) =>
    axiosInstance.get(`/deportes/catalogos/${recurso}`, { params: params(filtros) }).then((r) => r.data),
  crearCatalogo: (recurso: string, data: any) =>
    axiosInstance.post(`/deportes/catalogos/${recurso}`, data).then((r) => r.data),
  actualizarCatalogo: (recurso: string, id: number, data: any) =>
    axiosInstance.put(`/deportes/catalogos/${recurso}/${id}`, data).then((r) => r.data),
  eliminarCatalogo: (recurso: string, id: number) =>
    axiosInstance.delete(`/deportes/catalogos/${recurso}/${id}`).then((r) => r.data),

  // ============ AUXILIARES ============
  listarLocalidades: () => axiosInstance.get('/cLocalidadRoutes').then((r) => r.data),
  listarDisciplinas: () => axiosInstance.get<Disciplina[]>('/deportes/catalogos/disciplinas').then((r) => r.data),
  listarCategorias: () => axiosInstance.get<Categoria[]>('/deportes/catalogos/categorias').then((r) => r.data),
  listarRamas: () => axiosInstance.get<Rama[]>('/deportes/catalogos/ramas').then((r) => r.data),
  listarCategoriasInventario: () =>
    axiosInstance.get<CategoriaInventario[]>('/deportes/catalogos/categoria-inventario').then((r) => r.data),
};

export const API_UPLOADS = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

export default deportesApi;
